/**
 * datapoint-codec (v4 - multi-character suffixes)
 *
 * Compresses datapoints of the form `<prefix><digits><optional suffix>`
 * (e.g. "2033a", "2471", "2077j", "2033ab") into a URL-safe string. The
 * leading digit is redundant - one dataset always shares it and the URL
 * already carries it - so it is stripped on encode and supplied on decode.
 *
 * Packing
 * -------
 * One datapoint occupies a "slot" with `states` distinct values. G slots are
 * packed into one integer emitted as C base64url characters, where C is the
 * smallest width with `states^G <= 64^C`. `createCodec` derives the best G
 * rather than hard-coding it, and refuses any grouping whose products would
 * exceed Number.MAX_SAFE_INTEGER - all arithmetic here is exact, no BigInt.
 *
 * Two-letter suffixes: pick your strategy
 * ---------------------------------------
 * Widening every slot to hold any two-letter suffix means 1 + 26 + 26^2 = 703
 * suffix states, which costs 105 characters per 30 datapoints against a
 * 70-character baseline - a 50% tax on every URL to serve the ~3% of
 * datapoints that need it. Don't. Two better options, both supported here:
 *
 * 1. TABLE THE COMBOS THAT ACTUALLY OCCUR. `suffixes` accepts multi-character
 *    tokens, so "ab" can be its own state. A group of 3 in 7 characters holds
 *    64^7, i.e. 16,384 states per datapoint, and values and suffixes share
 *    that budget:
 *
 *        606 values x 27 = 16,362  ->  0 spare  ->  no room for combos
 *        400 values x 40 = 16,000  ->  13 two-letter combos, FREE
 *        300 values x 54 = 16,200  ->  27 two-letter combos, FREE
 *
 *    Below ~400 values the combos cost literally nothing. The catch: a combo
 *    that is not in the table cannot be encoded at all.
 *
 * 2. CONTINUATION ESCAPE (`continuation`). A second character is emitted as
 *    one extra slot, drawn from spare capacity in the slot space, so the
 *    common case pays nothing and each exception costs one slot (2.33 chars
 *    at 3-in-7). Any combination is representable. At the observed rate of
 *    14/500 datapoints, a 30-datapoint payload averages 72.3 characters and
 *    stays at or below 77 in 99% of cases.
 *
 * The two combine: table the frequent combos, keep the escape for the rest.
 *
 * NOTE: the encoded string is CASE-SENSITIVE, and the wire format depends on
 * `minValue`, `maxValue`, `suffixes` and `continuation`. Changing any of them
 * silently reinterprets old URLs - version your links if that is a risk.
 */

/** base64url alphabet - every character is RFC 3986 "unreserved". */
const ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-_';
const RADIX = 64;

const ALPHABET_INDEX: ReadonlyMap<string, number> = new Map(
  [...ALPHABET].map((c, i) => [c, i] as const),
);

const MAX_SAFE = Number.MAX_SAFE_INTEGER; // 2^53 - 1
const MAX_GROUP = 8;
const LETTERS = 'abcdefghijklmnopqrstuvwxyz';

export class DatapointCodecError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'DatapointCodecError';
  }
}

/** A datapoint, with or without its leading digit: "2033ab" or "033ab". */
export type Datapoint = string;

/** The shared leading digit of a dataset, as a digit 1-9 or its character. */
export type Prefix = number | string;

export interface ParsedDatapoint {
  /** The leading digit, or null when the input omitted it. */
  prefix: number | null;
  /** The prefix-stripped numeric part. */
  value: number;
  /** The full suffix ("a", "ab"), or null when there is none. */
  suffix: string | null;
}

export interface CodecOptions {
  /** Lowest value a datapoint can take, after the prefix is stripped. Default 0. */
  minValue?: number;
  /** Highest value a datapoint can take, after the prefix is stripped. Default 999. */
  maxValue?: number;
  /**
   * Suffix tokens, each its own state. A string is read as one token per
   * character; an array may contain multi-character tokens ("ab"), which is
   * how you buy two-letter combos out of spare slot capacity. Default "a".."z".
   */
  suffixes?: string | readonly string[];
  /**
   * Enables the continuation escape: characters listed here may follow any
   * single-character suffix token, costing one extra slot per occurrence.
   * Pass `true` for "a".."z". Default: disabled.
   */
  continuation?: string | boolean;
  /** Zero-padding width of the numeric part. Default: digits in `maxValue`. */
  digits?: number;
  /** Force a group size instead of deriving the optimum. */
  groupSize?: number;
  /**
   * Prepend a format stamp derived from every wire-affecting option, and
   * require it to match on decode. Costs 1 character (pass a number for more)
   * and converts a format change from SILENT MISREADING into a clean error -
   * see the note on format growth below. Strongly recommended if URLs are
   * persisted anywhere. Default: false, for compatibility with v1-v3 payloads.
   */
  stamp?: boolean | number;
  /**
   * Reject lists longer than this on encode, and payloads that decode to more
   * than this on decode. A cheap guard on URL length and on a malformed or
   * hostile query parameter expanding into unbounded work. Default: unlimited.
   */
  maxDatapoints?: number;
}

/**
 * Format growth - read before changing a live configuration
 * ---------------------------------------------------------
 * `states` is the radix the packer counts in, so ANY change to `minValue`,
 * `maxValue`, `suffixes` or `continuation` renumbers every slot. Adding one
 * suffix token to a live table does not append a state; it shifts all of them.
 * Measured: adding a 15th combo to a 14-combo table (values 1-300) left every
 * one of 20,000 existing payloads decoding without error and with different
 * values AND different suffixes. Silent, total corruption.
 *
 * Three ways to stay safe, best first:
 *
 * 1. USE `continuation` INSTEAD OF TABLING COMBOS. The escape covers all 676
 *    combinations from day one, so a combo you have never seen needs no config
 *    change and cannot invalidate anything.
 * 2. RESERVE SLOTS UP FRONT. If you must table combos, pad the table with
 *    placeholder tokens you never emit. The radix is then fixed on day one and
 *    a later combo simply claims a reserved index - verified to decode old
 *    payloads identically.
 * 3. SET `stamp: true`. One character that makes a mismatch loud instead of
 *    silent. It does not make old payloads readable; it stops you serving
 *    plausible nonsense while you find out.
 */

export interface CodecInfo {
  minValue: number;
  maxValue: number;
  digits: number;
  /** Suffix tokens that cost one slot each. */
  suffixes: readonly string[];
  /** Characters usable as an escaped second character; empty when disabled. */
  continuation: string;
  /** Distinct states one slot can take. */
  states: number;
  /** Slot states left unused by the chosen group width - free headroom. */
  spareStates: number;
  /** Slots packed into one integer. */
  groupSize: number;
  /** Characters emitted per full group. */
  charsPerGroup: number;
  /**
   * Amortised characters per slot. REPORTING ONLY - a diagnostic score, never
   * used to encode, decode, size or index anything.
   */
  charsPerSlot: number;
  /** Information-theoretic minimum per slot. Reporting only. */
  bitsPerSlot: number;
  /** Characters emitted for a partial group of i slots, index 0..groupSize. */
  partialWidths: readonly number[];
  /** Canonical description of everything that affects the wire format. */
  fingerprint: string;
  /** Format stamp prepended to every payload; empty when disabled. */
  stamp: string;
}

export interface DatapointCodec {
  readonly info: CodecInfo;
  encode(datapoints: readonly Datapoint[], options?: { prefix?: Prefix }): string;
  decode(encoded: string, prefix: Prefix): Datapoint[];
  decodeStripped(encoded: string): Datapoint[];
  decodeAt(encoded: string, index: number, prefix: Prefix): Datapoint;
  /** Slots a datapoint list will occupy (one each, plus one per escape). */
  slotsFor(datapoints: readonly Datapoint[]): number;
  /** Exact encoded length for a slot count - pair with `slotsFor`. */
  encodedLength(slots: number): number;
  /** Slots in an encoded string, from its length alone. */
  countSlots(encoded: string): number;
  /** Datapoints in an encoded string (<= countSlots when escapes are in use). */
  countDatapoints(encoded: string): number;
  isValidEncoded(encoded: string): boolean;
  parseDatapoint(datapoint: Datapoint): ParsedDatapoint;
  formatDatapoint(parsed: ParsedDatapoint): Datapoint;
}

/** Smallest base64url width holding `states ** group`, or Infinity if unsafe. */
function widthFor(states: number, group: number): number {
  if (group === 0) return 0;
  let capacity = 1;
  for (let i = 0; i < group; i++) {
    capacity *= states;
    if (capacity > MAX_SAFE) return Number.POSITIVE_INFINITY;
  }
  let width = 1;
  while (RADIX ** width < capacity) width++;
  return width;
}

function pow(states: number, group: number): number {
  let out = 1;
  for (let i = 0; i < group; i++) out *= states;
  return out;
}

/**
 * Largest per-slot state count a group of `group` slots in `width` characters
 * could hold: the integer floor of (64^width)^(1/group). Diagnostic only.
 */
function maxStatesForWidth(group: number, width: number): number {
  const target = 64n ** BigInt(width);
  const g = BigInt(group);
  let hi = 2n;
  while (hi ** g <= target) hi *= 2n;
  let lo = hi / 2n;
  while (lo < hi) {
    const mid = (lo + hi + 1n) / 2n;
    if (mid ** g <= target) lo = mid;
    else hi = mid - 1n;
  }
  return Number(lo);
}

function normalisePrefix(prefix: Prefix): number {
  const n = typeof prefix === 'string' ? Number(prefix) : prefix;
  if (!Number.isInteger(n) || n < 1 || n > 9) {
    throw new DatapointCodecError(
      `Invalid prefix ${JSON.stringify(prefix)}: expected a single digit 1-9.`,
    );
  }
  return n;
}

export function createCodec(options: CodecOptions = {}): DatapointCodec {
  const minValue = options.minValue ?? 0;
  const maxValue = options.maxValue ?? 999;
  const digits = options.digits ?? String(maxValue).length;
  const suffixes: readonly string[] =
    options.suffixes === undefined
      ? [...LETTERS]
      : typeof options.suffixes === 'string'
        ? [...options.suffixes]
        : [...options.suffixes];
  const continuation =
    options.continuation === true
      ? LETTERS
      : options.continuation === false || options.continuation === undefined
        ? ''
        : options.continuation;

  if (!Number.isInteger(minValue) || !Number.isInteger(maxValue) || minValue > maxValue) {
    throw new DatapointCodecError(
      `Invalid range ${minValue}-${maxValue}: expected integers with min <= max.`,
    );
  }
  if (minValue < 0) {
    throw new DatapointCodecError(`minValue must not be negative (got ${minValue}).`);
  }
  if (String(maxValue).length > digits) {
    throw new DatapointCodecError(
      `digits=${digits} cannot represent maxValue=${maxValue}.`,
    );
  }
  if (new Set(suffixes).size !== suffixes.length) {
    throw new DatapointCodecError('Suffix table contains duplicates.');
  }
  for (const token of suffixes) {
    if (!/^[a-z]+$/.test(token)) {
      throw new DatapointCodecError(
        `Suffix token ${JSON.stringify(token)} must be one or more letters a-z.`,
      );
    }
  }
  if (new Set(continuation).size !== continuation.length) {
    throw new DatapointCodecError('Continuation alphabet contains duplicates.');
  }
  if (continuation && !/^[a-z]*$/.test(continuation)) {
    throw new DatapointCodecError('Continuation alphabet must be letters a-z.');
  }

  const maxDatapoints = options.maxDatapoints ?? Number.POSITIVE_INFINITY;
  if (maxDatapoints !== Number.POSITIVE_INFINITY) {
    if (!Number.isInteger(maxDatapoints) || maxDatapoints < 0) {
      throw new DatapointCodecError(
        `maxDatapoints must be a non-negative integer (got ${options.maxDatapoints}).`,
      );
    }
  }

  const valueCount = maxValue - minValue + 1;
  const tokenStates = suffixes.length + 1; // + "no suffix"
  const baseStates = valueCount * tokenStates;
  const states = baseStates + continuation.length;
  if (states > MAX_SAFE) {
    throw new DatapointCodecError('Configuration is too large for this codec.');
  }

  // Pick the grouping with the lowest chars-per-slot ratio that stays in exact
  // integer arithmetic. Compared by cross-multiplication, never by division.
  let groupSize = 1;
  let charsPerGroup = widthFor(states, 1);
  if (options.groupSize !== undefined) {
    groupSize = options.groupSize;
    charsPerGroup = widthFor(states, groupSize);
    if (!Number.isFinite(charsPerGroup)) {
      throw new DatapointCodecError(
        `groupSize=${groupSize} exceeds exact integer arithmetic for ${states} states.`,
      );
    }
  } else {
    for (let g = 2; g <= MAX_GROUP; g++) {
      const w = widthFor(states, g);
      if (!Number.isFinite(w)) break;
      if (w * groupSize < charsPerGroup * g) {
        groupSize = g;
        charsPerGroup = w;
      }
    }
  }

  const partialWidths: number[] = [];
  for (let i = 0; i <= groupSize; i++) partialWidths.push(widthFor(states, i));

  // The slot count must follow from the string length alone.
  const widthToSlots = new Map<number, number>();
  for (let i = 0; i < groupSize; i++) {
    const w = partialWidths[i]!;
    if (i > 0 && w >= charsPerGroup) {
      throw new DatapointCodecError(
        `Ambiguous format: a partial group of ${i} needs ${w} chars, not fewer ` +
          `than a full group's ${charsPerGroup}.`,
      );
    }
    if (widthToSlots.has(w % charsPerGroup)) {
      throw new DatapointCodecError(
        `Ambiguous format: partial widths collide modulo ${charsPerGroup}.`,
      );
    }
    widthToSlots.set(w % charsPerGroup, i);
  }

  const limits = partialWidths.map((_, i) => pow(states, i));
  // How many more states per slot the chosen width would still hold - i.e. the
  // free headroom shared between the value range and the suffix table. BigInt
  // here only because 64^charsPerGroup can exceed 2^53; it runs once, at
  // construction, and never touches the packing arithmetic.
  const spareStates = maxStatesForWidth(groupSize, charsPerGroup) - states;

  // Everything that changes how bytes are interpreted, in a canonical order.
  const fingerprint = JSON.stringify({
    minValue,
    maxValue,
    digits,
    suffixes,
    continuation,
    states,
    groupSize,
    charsPerGroup,
  });
  const stampChars =
    options.stamp === undefined || options.stamp === false
      ? 0
      : options.stamp === true
        ? 1
        : options.stamp;
  if (!Number.isInteger(stampChars) || stampChars < 0 || stampChars > 4) {
    throw new DatapointCodecError(`stamp must be a boolean or an integer 0-4.`);
  }
  let stamp = '';
  {
    // FNV-1a, 32-bit. Not a security hash - it only has to change when the
    // format changes, so a stale payload is rejected instead of misread.
    let h = 0x811c9dc5;
    for (let i = 0; i < fingerprint.length; i++) {
      h ^= fingerprint.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    for (let i = 0; i < stampChars; i++) {
      stamp += ALPHABET[h % RADIX];
      h = Math.floor(h / RADIX);
    }
  }

  const tokenIndex = new Map(suffixes.map((t, i) => [t, i + 1] as const));
  const singleTokens = new Set(suffixes.filter((t) => t.length === 1));
  const continuationIndex = new Map([...continuation].map((c, i) => [c, i] as const));
  const pattern = new RegExp(`^([1-9])?([0-9]{${digits}})([a-z]*)$`);

  function parseDatapoint(datapoint: Datapoint): ParsedDatapoint {
    if (typeof datapoint !== 'string') {
      throw new DatapointCodecError(
        `Expected a string datapoint, received ${typeof datapoint}.`,
      );
    }
    const match = pattern.exec(datapoint.toLowerCase());
    if (match === null) {
      throw new DatapointCodecError(
        `Invalid datapoint ${JSON.stringify(datapoint)}: expected ${digits} digits ` +
          `with an optional leading digit 1-9 and an optional letter suffix.`,
      );
    }
    const value = Number(match[2]);
    if (value < minValue || value > maxValue) {
      throw new DatapointCodecError(
        `Datapoint ${JSON.stringify(datapoint)} has value ${value}, outside the ` +
          `configured range ${minValue}-${maxValue}.`,
      );
    }
    const suffix = match[3] === '' ? null : match[3]!;
    if (suffix !== null) resolveSuffix(suffix, datapoint); // validate early
    return { prefix: match[1] === undefined ? null : Number(match[1]), value, suffix };
  }

  /** Maps a suffix to a token index plus an optional escaped character. */
  function resolveSuffix(
    suffix: string,
    context: string,
  ): { token: number; escape: number | null } {
    const direct = tokenIndex.get(suffix);
    if (direct !== undefined) return { token: direct, escape: null };

    if (suffix.length === 2) {
      const head = tokenIndex.get(suffix[0]!);
      const tail = continuationIndex.get(suffix[1]!);
      if (head !== undefined && singleTokens.has(suffix[0]!) && tail !== undefined) {
        return { token: head, escape: tail };
      }
    }
    throw new DatapointCodecError(
      `Datapoint ${JSON.stringify(context)} has suffix ${JSON.stringify(suffix)}, ` +
        `which is not in the suffix table` +
        (continuation
          ? ` and cannot be escaped (continuation alphabet: "${continuation}").`
          : ` and the continuation escape is disabled.`) +
        ` Add it to \`suffixes\`, or enable \`continuation\`.`,
    );
  }

  function formatDatapoint({ prefix, value, suffix }: ParsedDatapoint): Datapoint {
    if (!Number.isInteger(value) || value < minValue || value > maxValue) {
      throw new DatapointCodecError(
        `Value ${value} is out of range (${minValue}-${maxValue}).`,
      );
    }
    if (suffix !== null) resolveSuffix(suffix, `${value}${suffix}`);
    const head = prefix === null ? '' : String(normalisePrefix(prefix));
    return `${head}${String(value).padStart(digits, '0')}${suffix ?? ''}`;
  }

  /** Datapoint -> one base slot, plus a continuation slot when escaped. */
  function toSlots(parsed: ParsedDatapoint, out: number[]): void {
    const { token, escape } =
      parsed.suffix === null
        ? { token: 0, escape: null }
        : resolveSuffix(parsed.suffix, String(parsed.value));
    out.push((parsed.value - minValue) * tokenStates + token);
    if (escape !== null) out.push(baseStates + escape);
  }

  function writeGroup(slots: readonly number[], from: number, count: number): string {
    let n = 0;
    for (let i = 0; i < count; i++) n = n * states + slots[from + i]!;
    let out = '';
    for (let shift = partialWidths[count]! - 1; shift >= 0; shift--) {
      out += ALPHABET[Math.floor(n / RADIX ** shift) % RADIX];
    }
    return out;
  }

  function readGroup(
    encoded: string,
    offset: number,
    count: number,
    out: number[],
  ): void {
    const limit = limits[count]!;
    let n = 0;
    for (let i = 0; i < partialWidths[count]!; i++) {
      const char = encoded[offset + i]!;
      const digit = ALPHABET_INDEX.get(char);
      if (digit === undefined) {
        throw new DatapointCodecError(
          `Invalid character ${JSON.stringify(char)} at position ${offset + i}.`,
        );
      }
      // Guard before multiplying so `n` can never leave the exact-integer range.
      if (n > Math.floor((limit - 1 - digit) / RADIX)) {
        throw new DatapointCodecError(
          `Corrupt chunk at position ${offset}: decodes above the configured range.`,
        );
      }
      n = n * RADIX + digit;
    }
    const group: number[] = [];
    for (let i = 0; i < count; i++) {
      const slot = n % states;
      n = (n - slot) / states;
      group.push(slot);
    }
    for (let i = group.length - 1; i >= 0; i--) out.push(group[i]!);
  }

  function encode(
    datapoints: readonly Datapoint[],
    encodeOptions: { prefix?: Prefix } = {},
  ): string {
    if (!Array.isArray(datapoints)) {
      throw new DatapointCodecError('Expected an array of datapoints.');
    }
    if (datapoints.length > maxDatapoints) {
      throw new DatapointCodecError(
        `Too many datapoints: ${datapoints.length} exceeds the configured ` +
          `maximum of ${maxDatapoints}.`,
      );
    }
    let expectedPrefix =
      encodeOptions.prefix === undefined ? null : normalisePrefix(encodeOptions.prefix);

    const slots: number[] = [];
    for (const datapoint of datapoints) {
      const parsed = parseDatapoint(datapoint);
      if (parsed.prefix !== null) {
        if (expectedPrefix === null) {
          expectedPrefix = parsed.prefix;
        } else if (parsed.prefix !== expectedPrefix) {
          throw new DatapointCodecError(
            `Datapoint ${JSON.stringify(datapoint)} has prefix ${parsed.prefix}, ` +
              `but this dataset uses prefix ${expectedPrefix}. A dataset must ` +
              `share one leading digit.`,
          );
        }
      }
      toSlots(parsed, slots);
    }

    let out = stamp;
    let i = 0;
    for (; i + groupSize <= slots.length; i += groupSize) {
      out += writeGroup(slots, i, groupSize);
    }
    if (i < slots.length) out += writeGroup(slots, i, slots.length - i);
    return out;
  }

  /** Validates and strips the format stamp, returning the bare payload. */
  function payloadOf(encoded: string): string {
    if (typeof encoded !== 'string') {
      throw new DatapointCodecError(
        `Expected an encoded string, received ${typeof encoded}.`,
      );
    }
    if (stamp === '') return encoded;
    if (!encoded.startsWith(stamp)) {
      throw new DatapointCodecError(
        `Format stamp mismatch: expected ${JSON.stringify(stamp)}, got ` +
          `${JSON.stringify(encoded.slice(0, stamp.length))}. This payload was ` +
          `built with a different value range, suffix table or continuation ` +
          `setting, and cannot be read by this configuration.`,
      );
    }
    return encoded.slice(stamp.length);
  }

  function decodeSlots(input: string): number[] {
    const encoded = payloadOf(input);
    const remainderWidth = encoded.length % charsPerGroup;
    const trailing = widthToSlots.get(remainderWidth);
    if (trailing === undefined) {
      throw new DatapointCodecError(
        `Encoded length ${encoded.length} is not a valid payload length for this ` +
          `format (groups of ${charsPerGroup} chars, optional tail of ` +
          `${[...widthToSlots.keys()].filter((w) => w !== 0).join(' or ')}).`,
      );
    }
    const out: number[] = [];
    const fullEnd = encoded.length - remainderWidth;
    for (let i = 0; i < fullEnd; i += charsPerGroup) readGroup(encoded, i, groupSize, out);
    if (trailing > 0) readGroup(encoded, fullEnd, trailing, out);
    return out;
  }

  /** Slot stream -> datapoints, folding continuation slots into their host. */
  function slotsToParsed(slots: readonly number[]): ParsedDatapoint[] {
    const out: ParsedDatapoint[] = [];
    for (let i = 0; i < slots.length; i++) {
      const slot = slots[i]!;
      if (slot >= baseStates) {
        const previous = out[out.length - 1];
        if (previous === undefined || previous.suffix === null) {
          throw new DatapointCodecError(
            `Malformed payload: continuation slot at index ${i} does not follow a ` +
              `suffixed datapoint.`,
          );
        }
        if (previous.suffix.length > 1) {
          throw new DatapointCodecError(
            `Malformed payload: two continuation slots in a row at index ${i}.`,
          );
        }
        const combined = previous.suffix + continuation[slot - baseStates]!;
        // If the table already has a state for this combination, the escape
        // form is a second encoding of the same datapoint. Reject it, so every
        // datapoint list has exactly one valid payload and payload equality
        // stays a safe proxy for data equality (cache keys, dedup, ETags).
        if (tokenIndex.has(combined)) {
          throw new DatapointCodecError(
            `Non-canonical payload: suffix ${JSON.stringify(combined)} is in the ` +
              `suffix table and must be encoded as one slot, not as an escape.`,
          );
        }
        previous.suffix = combined;
        continue;
      }
      if (out.length >= maxDatapoints) {
        throw new DatapointCodecError(
          `Payload decodes to more than the configured maximum of ` +
            `${maxDatapoints} datapoints.`,
        );
      }
      const token = slot % tokenStates;
      out.push({
        prefix: null,
        value: (slot - token) / tokenStates + minValue,
        suffix: token === 0 ? null : suffixes[token - 1]!,
      });
    }
    return out;
  }

  function countSlots(input: string): number {
    const encoded = payloadOf(input);
    return (
      Math.floor(encoded.length / charsPerGroup) * groupSize +
      (widthToSlots.get(encoded.length % charsPerGroup) ?? 0)
    );
  }

  return {
    info: {
      minValue,
      maxValue,
      digits,
      suffixes,
      continuation,
      states,
      spareStates,
      groupSize,
      charsPerGroup,
      charsPerSlot: charsPerGroup / groupSize,
      bitsPerSlot: Math.log2(states),
      partialWidths,
      fingerprint,
      stamp,
    },
    encode,
    decode: (encoded, prefix) => {
      const head = normalisePrefix(prefix);
      return slotsToParsed(decodeSlots(encoded)).map((p) =>
        formatDatapoint({ ...p, prefix: head }),
      );
    },
    decodeStripped: (encoded) =>
      slotsToParsed(decodeSlots(encoded)).map((p) => formatDatapoint(p)),
    decodeAt: (encoded, index, prefix) => {
      const head = normalisePrefix(prefix);
      // With escapes enabled a datapoint index is not a slot index, so fall
      // back to a full decode; otherwise slice just the group that holds it.
      if (continuation) {
        const all = slotsToParsed(decodeSlots(encoded));
        const hit = all[index];
        if (!Number.isInteger(index) || index < 0 || hit === undefined) {
          throw new DatapointCodecError(`Index ${index} is out of bounds.`);
        }
        return formatDatapoint({ ...hit, prefix: head });
      }
      if (!Number.isInteger(index) || index < 0 || index >= countSlots(encoded)) {
        throw new DatapointCodecError(`Index ${index} is out of bounds.`);
      }
      const payload = payloadOf(encoded);
      const offset = Math.floor(index / groupSize) * charsPerGroup;
      const width = Math.min(charsPerGroup, payload.length - offset);
      const group = slotsToParsed(
        decodeSlots(stamp + payload.slice(offset, offset + width)),
      );
      return formatDatapoint({ ...group[index % groupSize]!, prefix: head });
    },
    slotsFor: (datapoints) => {
      const slots: number[] = [];
      for (const d of datapoints) toSlots(parseDatapoint(d), slots);
      return slots.length;
    },
    encodedLength: (slots) =>
      stamp.length +
      Math.floor(slots / groupSize) * charsPerGroup +
      partialWidths[slots % groupSize]!,
    countSlots,
    countDatapoints: (encoded) =>
      continuation ? slotsToParsed(decodeSlots(encoded)).length : countSlots(encoded),
    isValidEncoded: (encoded) => {
      try {
        slotsToParsed(decodeSlots(encoded));
        return true;
      } catch {
        return false;
      }
    },
    parseDatapoint,
    formatDatapoint,
  };
}
